import React from 'react';
import {useTranslation} from 'react-i18next';
import {promptAskPhrase} from '../../../utils/promptAskPhrase';
import {ArrowUpRight, ChatCenteredText, Globe} from '../../icons';
import {MENU_EDIT, PromptRowMenu} from './PromptRowMenu';
import {relativeUse} from './relativeUse';

const RAW = {interpolation: {escapeValue: false}};

/**
 * Una fila de la solapa propia: el nombre (que es lo que se le dice a la IA) y lo que devuelve, sus
 * etiquetas (adaptado de Dashbo, publicado en el portal), cuándo lo usó la IA por última vez, «Copiar
 * frase» y el menú «···». Quién lo creó está en el menú: repetido en cada fila no decía nada.
 *
 * La acción principal copia la FRASE del pedido, no el texto: el texto vive en Dashbo y la IA trae
 * siempre la última versión. Antes de copiar, el botón muestra la frase exacta (al pasar el mouse o con
 * el foco), porque «Copiar pedido» copiaba algo que no se veía. El texto sigue a mano en el menú.
 */
export function PromptRow({prompt, highlighted, pending, now, onCopyAsk, onAction, onTogglePortal}) {
    const {t} = useTranslation();
    const use = relativeUse(prompt.last_mcp_use, now);
    const source = prompt.source;
    const adapted = Boolean(source && source.title);
    const baseUpdated = Boolean(source && source.base_updated);
    const published = Boolean(prompt.portal_visible);
    const tipId = `agp-ask-tip-${prompt.id}`;

    return (
        <div id={`agp-row-${prompt.id}`}
             className={`agp-row${highlighted ? ' -highlight' : ''}${pending ? ' -pending' : ''}`}>
            <div className="agp-row__main">
                <div className="agp-row__name">{prompt.title}</div>
                {prompt.value_preview ? (
                    <div className="agp-row__preview">{prompt.value_preview}</div>
                ) : prompt.is_editable && (
                    <div className="agp-row__preview -empty">
                        {t('agency_prompts.no_description')}{' '}
                        <button type="button" className="agp-row__add-desc" disabled={pending}
                                onClick={() => onAction(MENU_EDIT, prompt)}>
                            {t('agency_prompts.add_description')}
                        </button>
                    </div>
                )}
                {(adapted || baseUpdated || published) && (
                    <div className="agp-row__chips">
                        {adapted && (
                            <span className="agp-chip -violet">
                                <ArrowUpRight size={12} weight="bold" aria-hidden="true"/>
                                {t('agency_prompts.adapted_from', {title: source.title, ...RAW})}
                            </span>
                        )}
                        {baseUpdated && <span className="agp-chip -amber">{t('agency_prompts.base_updated')}</span>}
                        {published && (
                            <span className="agp-chip -green">
                                <Globe size={12} weight="bold" aria-hidden="true"/>
                                {t('agency_prompts.in_portal')}
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div className="agp-row__use">
                <span className={`agp-used${use.used ? '' : ' -never'}`}>
                    {t(use.key, use.count !== undefined ? {count: use.count} : undefined)}
                </span>
            </div>
            <div className="agp-row__actions">
                <span className="info-icon-container agp-ask">
                    <button type="button" className="agp-btn -ghost" onClick={() => onCopyAsk(prompt)}
                            disabled={pending} aria-describedby={tipId}>
                        <ChatCenteredText size={15} weight="bold" aria-hidden="true"/>
                        {t('agency_prompts.copy_ask')}
                    </button>
                    <span className="warning-tooltip -below-right -rich agp-ask__tip" role="tooltip" id={tipId}>
                        <span className="agp-ask__label">{t('agency_prompts.copy_ask_preview')}</span>
                        <span className="agp-ask__phrase">{promptAskPhrase(prompt.title, prompt.client)}</span>
                        <span className="agp-ask__note">
                            {prompt.client ? t('agency_prompts.copy_ask_note_client') : t('agency_prompts.copy_ask_note_all')}
                        </span>
                    </span>
                </span>
                {/* Con la baja en curso, el menú no se abre: un segundo «Desactivar» daría 404. */}
                <PromptRowMenu prompt={prompt} disabled={pending} onAction={onAction} onTogglePortal={onTogglePortal}/>
            </div>
        </div>
    );
}
